<script setup lang="ts">
/**
 * The till, composed.
 *
 * Holds no rules of its own: it wires the session's view model to the
 * components and forwards intents back. Every affordance shown here was
 * decided in `session.ts`, and every string comes from the catalogue.
 *
 * The layout is a product grid beside a check panel, which is the arrangement
 * every server already knows from every POS they have used. Novelty in this
 * screen costs training time and buys nothing.
 */
import { computed, defineAsyncComponent, ref, watch } from "vue";
// Type only: the choices library stays in the lazy sheet's chunk, off the
// startup path, and the one rule this screen needs is written out below.
import type { MenuChoice } from "@gf/module-catalog/choices";
import { canApprove, needsApproval } from "../approvals";
import type { Translator } from "@gf/kernel/i18n";
import type { DegradationReason, SellState } from "@gf/sync-client";
import StatusBanner from "./StatusBanner.vue";
import StaffLogin, { type LoginCandidate } from "./StaffLogin.vue";

/**
 * EVERYTHING BEHIND THE SIGN-IN IS A LAZY CHUNK.
 *
 * The first thing a till paints is the login screen — nothing else is reachable
 * until somebody signs in, because every event carries the actor and an
 * unattributed sale makes void-abuse detection meaningless. So the floor, the
 * grid, the check panel and both dialogs are not startup cost.
 *
 * This is only safe because the service worker precaches every emitted chunk:
 * "lazy" here means "not in the startup chunk", not "needs a network". A till
 * that has not been online since its last update already has them, and the load
 * happens while a server is typing six digits.
 */
import DialogSkeleton from "./DialogSkeleton.vue";

/**
 * A DELAY AND SOMETHING TO SEE, because `defineAsyncComponent` on its own shows
 * NOTHING.
 *
 * Eleven components behind a dynamic import, none of them with a
 * `loadingComponent` or a `delay`. On a till with a slow connection a server
 * pressed "Zaplatit" and the screen did not change — which is
 * indistinguishable from the press not having registered, and the second press
 * is how you get two dialogs' worth of confusion during a rush.
 *
 * `delay: 120` matters as much as the placeholder. Below about that, a chunk
 * that resolves immediately — which is every load after the first, since the
 * service worker precaches all of them — would flash a skeleton and remove it
 * in the same breath. That flicker reads as a fault.
 */
const lazyDialog = (loader: () => Promise<unknown>) =>
  defineAsyncComponent({
    loader: loader as never,
    delay: 120,
    loadingComponent: DialogSkeleton,
  });

/**
 * The order screen — the menu and the check — in its own chunk, off the
 * startup path. This component routes between sign-in, the floor, a table and
 * the drawer, and owns the dialogs; it does not lay out a table.
 */
const OrderView = defineAsyncComponent(() => import("./OrderView.vue"));
const VoidDialog = lazyDialog(() => import("./VoidDialog.vue"));
const AllowanceDialog = lazyDialog(() => import("./AllowanceDialog.vue"));
/**
 * Lazy, like the others, and for the same reason the bundle budget carries:
 * most checks never ask for a full document, and this pulls in the IČO
 * checksum with it.
 */
const BuyerDialog = lazyDialog(() => import("./BuyerDialog.vue"));
/**
 * LAZY, like every other dialog here — and I argued the opposite first.
 *
 * I made this a static import and raised the bundle budget for it, reasoning
 * that a till installed and taken offline within minutes would have an age gate
 * it could not open. That reasoning is not wrong, and it applies IDENTICALLY to
 * `TenderDialog` — you cannot take money without it — which has been lazy since
 * it was written. Singling this one out was inconsistent with the app's own
 * convention for no gain the others do not also need.
 *
 * The service worker precaches every chunk. The window where a lazy dialog is
 * unavailable is the window before the first successful load, in which a till
 * cannot sign in either.
 */
const AgeCheckDialog = lazyDialog(() => import("./AgeCheckDialog.vue"));
const TenderDialog = lazyDialog(() => import("./TenderDialog.vue"));
const ReceiptDialog = lazyDialog(() => import("./ReceiptDialog.vue"));
const FloorPlan = defineAsyncComponent(() => import("./FloorPlan.vue"));
const CounterHome = defineAsyncComponent(() => import("./CounterHome.vue"));
import type { ReadyOrder } from "./CounterHome.vue";
const ModifierSheet = lazyDialog(() => import("./ModifierSheet.vue"));
const TransferDialog = lazyDialog(() => import("./TransferDialog.vue"));
// The QR encoder comes with it, and a code is shown when somebody asks for one.
const GuestCodeDialog = lazyDialog(() => import("./GuestCodeDialog.vue"));

/**
 * LAZY, and only because the service worker now precaches every emitted chunk.
 *
 * Counting in and counting out happen twice a shift; ringing an item happens
 * four hundred times. Keeping the drawer, its count sheet and the Z-report out
 * of the startup chunk is 46 kB a server does not wait for at the start of
 * every shift.
 *
 * This would have been a REGRESSION a commit ago. The worker precached only the
 * documents, so a lazily-fetched chunk would have been unavailable offline —
 * and the moment it is needed most, counting out at 02:00, is exactly when a
 * venue's connection is least reliable. Splitting a PWA whose worker cannot
 * precache the split is not an optimisation.
 */
const DrawerScreen = lazyDialog(() => import("./DrawerScreen.vue"));
const ReceiptsScreen = lazyDialog(() => import("./ReceiptsScreen.vue"));
import type { CheckSummary, CheckView, MenuSnapshot } from "../session";
import type { FloorEntry } from "../checkbook";
import type { FloorBooking } from "../bookings";
import type { ReceiptBook } from "../receipts-client";
import type { CountSheet, DenominationCount } from "@gf/module-cash/shift";
import type { ZReport } from "../drawer";
import type {
  AllowanceReasonCode,
  ModeChangeReasonCode,
  OFFERED_TENDER_KINDS,
  TenderVoidReasonCode,
  VoidReasonCode,
} from "../locales/codes";

const props = defineProps<{
  check: CheckView;
  menu: MenuSnapshot;
  /**
   * Every check on this order. One entry on an unsplit table.
   *
   * OPTIONAL, and absent means unsplit rather than broken: a caller that has
   * not opened a table has no checks to describe, and half the specs in this
   * file construct the screen to exercise something else entirely.
   */
  checks?: readonly CheckSummary[];
  /** What the kitchen has run out of. Passed straight to the grid. */
  soldOut?: ReadonlySet<string>;
  t: Translator;
  currency: string;
  /**
   * At or above this the document must name the buyer. Null before the signed
   * bundle verifies — the dialog says "optional" rather than guessing.
   */
  simplifiedLimitMinor?: bigint | null;
  /** A buyer lookup is in flight. */
  buyerBusy?: boolean;
  /** What the register said, already translated. */
  buyerNotice?: string | null;
  /** What a successful lookup returned. */
  buyerFound?: {
    legalName: string;
    registrationId: string;
    vatId: string | null;
    address: string | null;
  } | null;
  sellState: SellState;
  reasons: readonly DegradationReason[];
  queuedCount: number;
  /**
   * The last refusal, as a message key.
   *
   * Every session method already returned one and nothing rendered it, so a
   * refused tender or an unknown item produced silence. A till that ignores a
   * tap without saying why teaches staff it is broken.
   */
  error?: { key: string; params?: Readonly<Record<string, string>> } | null;
  /**
   * The floor, or an empty list before the config bundle lands.
   *
   * When nothing is selected the till shows this instead of the product grid —
   * a server returns here between every table, so it is the resting state
   * rather than a modal somebody has to find.
   */
  floor?: readonly FloorEntry[];
  /** The table being served, or null while the floor is showing. */
  activeTable?: string | null;
  /** Open checks with no table. */
  tabs?: readonly FloorEntry[];
  /** Ticks, for the minutes a table has been open. */
  now?: Date;
  /** staffId → name, for initials on the floor. */
  staffNames?: Readonly<Record<string, string>>;
  /** Who is holding the handheld, or null when nobody has signed in. */
  signedIn?: { staffId: string | null; displayName: string | null; roleId?: string | null } | null;
  staff?: readonly LoginCandidate[];
  loginError?: string | null;
  lockedForSeconds?: number;
  loginBusy?: boolean;
  /** From the regulatory registry (K11). CZ: whole koruna. */
  cashIncrementMinor?: bigint;

  /**
   * The drawer.
   *
   * A blind sheet, whether a shift is open, and the Z once it has been counted
   * — nothing that could carry an expectation before the count.
   */
  drawerSheet?: CountSheet;
  drawerOpen?: boolean;
  drawerReport?: ZReport | null;
  /** Who counted, by name. The Z-report falls back to the staff id. */
  cashierName?: string | null;
  /** The languages this build carries. Absent leaves the control off. */
  locales?: readonly string[];
  locale?: string;
  /** The receipt just issued, or null. See `ReceiptDialog`. */
  receipt?: {
    lines: readonly string[] | null;
    refusalKey: string | null;
    warningKeys: readonly string[];
  } | null;
  /** The printer *Tisk* sends receipts to, by name; null prints through the browser. */
  receiptPrinter?: string | null;
  receiptPrint?: "sending" | "sent" | "failed" | null;
  /**
   * Sales this till has queued and not yet handed over.
   *
   * Travels from the composition root to the Z-report untouched. The line it
   * feeds had copy in four languages and four passing tests and no supplier,
   * so it could never render.
   */
  fiscalOwed?: number;
  /**
   * The URL a guest's phone should open for THIS check, minted locally.
   *
   * Null when the till has no verified config bundle — the venue key that signs
   * the code lives in it, so a till that has never synced genuinely cannot mint
   * one. The dialog says that rather than showing an empty square.
   */
  guestUrl?: string | null;
  /** An age check the session is waiting on, named. Null when none is owed. */
  ageCheck?: { items: string; selectionIds: readonly string[] } | null;
  ageBusy?: boolean;
  drawerError?: string | null;
  drawerBusy?: boolean;
  /** The training venue: practice, and said so on every screen. */
  training?: boolean;
  /** The venue's clock, for the floor. */
  clock?: { readonly timezone: string; readonly businessDayStartMinutes: number } | null;
  /** A shift left open on this drawer from an earlier day. */
  drawerStale?: { readonly businessDate: string; readonly cashierName: string | null } | null;
  /** Today's receipts, while that screen is up. Null when it is not. */
  receiptBook?: ReceiptBook | null;
  /** A counter till: its letter, its open sales, what is ready at the pass. Null at the tables. */
  counter?: {
    prefix: string;
    openSales: readonly FloorEntry[];
    ready: readonly ReadyOrder[];
    tables: boolean;
  } | null;
  /** Tables whose food is on the pass. */
  readyTables?: readonly string[];
  /** Today's bookings, for the floor's "who is coming". */
  bookings?: readonly FloorBooking[];
  /** The number just paid for at the counter, said with its receipt. */
  pickupShown?: string | null;
  /** The venue serves tables in courses: the order screen offers them. */
  courses?: boolean;
}>();

const emit = defineEmits<{
  ring: [payload: { itemRef: string; choices?: MenuChoice[]; note?: string; courseId?: string }];
  fireCourse: [courseId: string];
  void: [payload: { selectionId: string; reasonCode: VoidReasonCode; approval?: { staffId: string; pin: string } }];
  quantity: [payload: { selectionId: string; quantityMilli: number }];
  setMode: [payload: { mode: "DINE_IN" | "TAKEAWAY"; reasonCode: ModeChangeReasonCode }];
  selectCheck: [checkId: string];
  splitEvenly: [ways: number];
  unsplit: [];
  moveSelection: [payload: { selectionId: string; toCheckId: string }];
  allowance: [
    payload: {
      selectionId: string;
      kind: "DISCOUNT" | "COMP";
      amountMinor: bigint;
      reasonCode: AllowanceReasonCode;
      approval?: { staffId: string; pin: string };
    },
  ];
  send: [];
  tender: [{ kind: (typeof OFFERED_TENDER_KINDS)[number]; offeredMinor?: bigint; tipMinor?: bigint; closeAfter?: boolean }];
  reverseTender: [{ tenderId: string; reasonCode: TenderVoidReasonCode }];
  closeCheck: [];
  /** An IČO to look up. The venue key never leaves the server. */
  lookupBuyer: [registrationId: string];
  identifyBuyer: [
    payload: {
      legalName: string;
      registrationId: string | null;
      vatId: string | null;
      address: string | null;
      source: "ARES" | "MANUAL";
    },
  ];
  dismissError: [];
  openTable: [tableRef: string];
  /** A booked party, seated at a free table. */
  seat: [payload: { bookingId: string; tableRef: string }];
  openTab: [];
  transfer: [tableRef: string];
  backToFloor: [];
  confirmAge: [];
  cancelAge: [];
  signIn: [{ staffId: string; pin: string }];
  signOut: [];
  openDrawer: [readonly DenominationCount[]];
  countDrawer: [readonly DenominationCount[]];
  payOut: [{ amountMinor: bigint; reasonCode: string }];
  closeDrawer: [];
  closeStale: [];
  locale: [code: string];
  closeReceipt: [];
  printReceipt: [];
  openReceipts: [];
  closeReceipts: [];
  selectReceipt: [checkId: string];
  refundReceipt: [payload: { reasonCode: string; approval?: { staffId: string; pin: string } }];
  startCounter: [mode: "DINE_IN" | "TAKEAWAY"];
  handOver: [orderId: string];
  handOverTable: [tableRef: string];
}>();

/** A counter till looking at the floor instead of the counter. */
const floorFromCounter = ref(false);

/**
 * Whether the drawer screen is up.
 *
 * Local, because it is a place the server has navigated to rather than a fact
 * about the venue. Which STAGE the drawer shows is derived from the session's
 * fold, not from here.
 */
const drawer = ref(false);

/**
 * The language is chosen at sign-in and on the floor, not mid-order: at a
 * table the strip is the status line alone, and the table's own bar is below.
 */
const showLang = computed(
  () => (props.locales?.length ?? 0) > 1 && !(props.signedIn?.staffId && props.activeTable),
);

/** Which line the void dialog is for, or null when it is closed. */
const voiding = ref<string | null>(null);
/** Which line money is being taken off, or null when the dialog is closed. */
const allowing = ref<string | null>(null);
const naming = ref(false);
const showingGuestCode = ref(false);
const moving = ref(false);



/** "Stůl 4", "Zahrádka 2", "Účet 3" — the name a person uses, never the ref. */
const tableName = computed(() => {
  const ref = props.activeTable;
  if (!ref) return "";
  // A counter sale is its number, and how it is eaten.
  if (ref.startsWith("pickup:")) {
    return `${ref.slice("pickup:".length)} · ${props.t.t(props.check.consumptionMode === "TAKEAWAY" ? "pos.counter.toGo" : "pos.counter.here")}`;
  }
  const entry = [...(props.floor ?? []), ...(props.tabs ?? [])].find((t) => t.tableRef === ref);
  if (!entry) return ref;
  // A tab or a check held apart already carries its whole name.
  return entry.area === "" ? entry.name : props.t.t("pos.check.table", { table: entry.name });
});

/** Which item the modifier sheet is open for, or null. */
const choosing = ref<string | null>(null);
const choosingItem = computed(() => props.menu.items.find((i) => i.itemRef === choosing.value));

/** A plain tap rings at once, unless the dish cannot be made without a choice. */
/** The course what is rung next goes out with, at a table that uses courses. Per table. */
const course = ref<string | null>(null);
watch(
  () => props.activeTable,
  () => {
    course.value = null;
  },
);
const withCourse = (): { courseId?: string } => (course.value ? { courseId: course.value } : {});

const ring = (itemRef: string): void => {
  const item = props.menu.items.find((i) => i.itemRef === itemRef);
  if (item && item.modifierGroups.some((g) => g.minSelections > 0)) choosing.value = itemRef;
  else emit("ring", { itemRef, ...withCourse() });
};

const confirmChoices = (payload: { choices: MenuChoice[]; note?: string }): void => {
  if (choosing.value) emit("ring", { itemRef: choosing.value, ...payload, ...withCourse() });
  choosing.value = null;
};

/**
 * Whether the payment screen is up.
 *
 * It stays up after a partial payment — part stravenka then cash is one
 * continuous act at the counter, and dropping back to the grid between the two
 * makes the server re-find a check they never left.
 */
const paying = ref(false);

const allowingLine = computed(() =>
  props.check.lines.find((l) => l.selectionId === allowing.value),
);

/** Who may say yes on this till: the managers on its roster, not the person asking. */
const approvers = computed(() =>
  (props.staff ?? [])
    .filter((p) => canApprove(p.roleId) && p.staffId !== props.signedIn?.staffId)
    .map((p) => ({ staffId: p.staffId, displayName: p.displayName })),
);
const voidNeedsApproval = computed(() => {
  const line = props.check.lines.find((l) => l.selectionId === voiding.value);
  const sent = line !== undefined && line.fulfillmentState !== "NEW" && line.fulfillmentState !== "HELD";
  return needsApproval(sent ? "VOID_SENT" : "VOID_UNSENT", props.signedIn?.roleId);
});
const allowanceNeedsApproval = computed(() => needsApproval("ALLOWANCE", props.signedIn?.roleId));
// A comp is a manager's: a shift lead may discount, not give a dish away.
const compNeedsApproval = computed(() => needsApproval("COMP", props.signedIn?.roleId));
const compApprovers = computed(() =>
  (props.staff ?? [])
    .filter((p) => canApprove(p.roleId, "COMP") && p.staffId !== props.signedIn?.staffId)
    .map((p) => ({ staffId: p.staffId, displayName: p.displayName })),
);
const refundNeedsApproval = computed(() => needsApproval("REFUND", props.signedIn?.roleId));
const refundApprovers = computed(() =>
  (props.staff ?? [])
    .filter((p) => canApprove(p.roleId, "REFUND") && p.staffId !== props.signedIn?.staffId)
    .map((p) => ({ staffId: p.staffId, displayName: p.displayName })),
);
const staffNames = computed(() =>
  Object.fromEntries((props.staff ?? []).map((p) => [p.staffId, p.displayName])),
);

const confirmAllowance = (payload: {
  kind: "DISCOUNT" | "COMP";
  amountMinor: bigint;
  reasonCode: AllowanceReasonCode;
  approval?: { staffId: string; pin: string };
}): void => {
  if (allowing.value) emit("allowance", { selectionId: allowing.value, ...payload });
  allowing.value = null;
};

const voidingName = computed(
  () => props.check.lines.find((l) => l.selectionId === voiding.value)?.name ?? "",
);

// A till that cannot sell must not accept a ring. Disabling the grid is
// clearer than accepting the tap and failing afterwards.
const cannotSell = computed(() => props.sellState === "cannot_sell");

const confirmVoid = (reasonCode: VoidReasonCode, approval?: { staffId: string; pin: string }): void => {
  if (voiding.value) emit("void", { selectionId: voiding.value, reasonCode, ...(approval ? { approval } : {}) });
  voiding.value = null;
};

const closeAndDismiss = (): void => {
  emit("closeCheck");
  paying.value = false;
};
</script>

<template>
  <div class="pos">
    <!--
      THE LANGUAGE, ON EVERY SCREEN INCLUDING SIGN-IN.

      A till in a Czech pub stays in Czech — the deployment pins it, and the
      device's own preference is the wrong authority. But a venue hires, and
      the person holding the handheld tonight may read Ukrainian better than
      Czech; making them sign in first to find the setting is making them read
      the one screen they cannot get past.

      Two buttons, not a select: a select on a touchscreen opens a native
      wheel that covers the screen, and there are four options.

      `aria-pressed` rather than a class, so the current language is announced
      rather than shown — this control is deliberately small, and small is
      exactly where colour-only state fails.
    -->
    <!-- Not at a table: there it sat over the table's own bar, and the
         language is chosen at sign-in and on the floor, not mid-order. -->
    <!--
      ONE STRIP ALONG THE TOP: the till's health on the left, the language on
      the right. The switch used to float over the corner at 44 × 28, across
      the status line and the header beneath it — too small for a thumb and on
      top of whatever sat there. In the strip it is a real row with real
      targets, and the strip grows only on the screens that carry it.
    -->
    <div v-if="showLang || signedIn?.staffId" class="pos__top">
      <StatusBanner
        v-if="signedIn?.staffId"
        class="pos__banner"
        :state="sellState"
        :reasons="reasons"
        :queued-count="queuedCount"
        :training="training"
        :t="t"
      />
      <div
        v-if="showLang"
        class="pos__lang"
        role="group"
        :aria-label="t.t('pos.language')"
      >
        <button
          v-for="code in locales"
          :key="code"
          type="button"
          class="pos__lang-btn"
          :aria-pressed="code === locale"
          @click="$emit('locale', code)"
        >
          {{ code.toUpperCase() }}
        </button>
      </div>
    </div>

    <!-- NOTHING ELSE IS REACHABLE UNTIL SOMEBODY SIGNS IN. Not because the
         screen is secret — the roster is who is standing in this room — but
         because every event carries the actor, and an unattributed sale makes
         void-abuse detection and the fraud statistics meaningless. -->
    <StaffLogin
      v-if="!signedIn?.staffId"
      :staff="staff ?? []"
      :t="t"
      :error="loginError"
      :locked-for-seconds="lockedForSeconds"
      :busy="loginBusy"
      @sign-in="$emit('signIn', $event)"
    />

    <template v-else>
    <!-- Above the body rather than inside a dialog: a server holding a plate
         needs to see it without dismissing anything, and a toast that fades
         is a message nobody read. -->
    <p v-if="error" class="pos__error" role="alert">
      <span>{{ t.t(error.key, error.params ?? {}) }}</span>
      <button type="button" class="pos__error-dismiss" @click="$emit('dismissError')">
        {{ t.t("pos.action.dismiss") }}
      </button>
    </p>

    <DrawerScreen
      v-if="drawer"
      class="pos__drawer"
      :t="t"
      :currency="currency"
      :sheet="drawerSheet!"
      :open="drawerOpen ?? false"
      :report="drawerReport"
      :cashier-name="cashierName ?? null"
      :fiscal-owed="fiscalOwed"
      :error="drawerError"
      :busy="drawerBusy"
      :stale-shift="drawerStale ?? null"
      :open-tables="[...(floor ?? []), ...(tabs ?? [])].filter((f) => f.occupied).map((f) => f.name)"
      @close-stale="$emit('closeStale')"
      @open-shift="$emit('openDrawer', $event)"
      @count="$emit('countDrawer', $event)"
      @pay-out="$emit('payOut', $event)"
      @close-shift="
        $emit('closeDrawer');
        drawer = false;
      "
      @back="drawer = false"
    />

    <ReceiptsScreen
      v-else-if="receiptBook"
      class="pos__receipts"
      :t="t"
      :currency="currency"
      :time-zone="receiptBook.timeZone"
      :receipts="receiptBook.receipts"
      :error-key="receiptBook.errorKey"
      :selected-id="receiptBook.selectedId"
      :paper="receiptBook.paper"
      :corrective="receiptBook.corrective"
      :refund-tenders="receiptBook.refundTenders"
      :table-names="receiptBook.tableNames"
      :staff-names="staffNames"
      :needs-approval="refundNeedsApproval"
      :approvers="refundApprovers"
      :busy="receiptBook.busy"
      @back="$emit('closeReceipts')"
      @select="$emit('selectReceipt', $event)"
      @refund="(reasonCode: string, approval?: { staffId: string; pin: string }) => $emit('refundReceipt', approval ? { reasonCode, approval } : { reasonCode })"
      @print="$emit('printReceipt')"
    />

    <!-- A counter till rests on the counter: here or to go, open sales, the pass. -->
    <CounterHome
      v-else-if="counter && !floorFromCounter && (activeTable === null || activeTable === undefined)"
      class="pos__floor"
      :t="t"
      :currency="currency"
      :prefix="counter.prefix"
      :open-sales="counter.openSales"
      :ready="counter.ready"
      :now="now"
      :disabled="cannotSell"
      :tables="counter.tables"
      @start="$emit('startCounter', $event)"
      @resume="$emit('openTable', $event)"
      @hand-over="$emit('handOver', $event)"
      @floor="floorFromCounter = true"
    >
      <template #action>
        <button
          v-if="drawerSheet"
          type="button"
          class="pos__back"
          :class="{ 'pos__back--attention': !drawerOpen }"
          @click="drawer = true"
        >
          {{ t.t("pos.drawer.title") }}<template v-if="!drawerOpen"> · {{ t.t("pos.drawer.countIn") }}</template>
        </button>
        <button type="button" class="pos__back" data-testid="open-receipts" @click="$emit('openReceipts')">
          {{ t.t("pos.receipts.title") }}
        </button>
        <button type="button" class="pos__back" @click="$emit('signOut')">
          {{ signedIn?.displayName }} · {{ t.t("pos.login.signOut") }}
        </button>
      </template>
    </CounterHome>

    <!-- The floor is the resting state. A till with no table selected shows
         the room, not an empty check nobody can ring against. -->
    <FloorPlan
      v-else-if="activeTable === null || activeTable === undefined"
      class="pos__floor"
      :floor="floor ?? []"
      :t="t"
      :currency="currency"
      :disabled="cannotSell"
      :tabs="tabs"
      :now="now"
      :staff-names="staffNames"
      :clock="clock ?? null"
      :ready-tables="readyTables ?? []"
      :bookings="bookings ?? []"
      @open-table="$emit('openTable', $event)"
      @seat="$emit('seat', $event)"
      @open-tab="$emit('openTab')"
    >
      <template #action>
        <button v-if="counter" type="button" class="pos__back" data-testid="back-to-counter" @click="floorFromCounter = false">
          {{ t.t("pos.counter.back") }}
        </button>
        <!-- ON THE FLOOR, NOT BEHIND A MENU. Counting in is the first thing
             that happens in a shift and counting out is the last; a drawer
             reachable only through a settings screen is a drawer nobody counts
             until the manager asks why the month does not add up. -->
        <button
          v-if="drawerSheet"
          type="button"
          class="pos__back"
          :class="{ 'pos__back--attention': !drawerOpen }"
          @click="drawer = true"
        >
          {{ t.t("pos.drawer.title") }}<template v-if="!drawerOpen"> · {{ t.t("pos.drawer.countIn") }}</template>
        </button>
        <button type="button" class="pos__back" data-testid="open-receipts" @click="$emit('openReceipts')">
          {{ t.t("pos.receipts.title") }}
        </button>
        <button type="button" class="pos__back" @click="$emit('signOut')">
          {{ signedIn?.displayName }} · {{ t.t("pos.login.signOut") }}
        </button>
      </template>
    </FloorPlan>

    <!-- Leaving a table is ONE TAP and never settles anything. A server walks
         away from an open check constantly — to the bar, to the kitchen, to
         another table — and a till that made that hard would be worked around
         within a shift. -->
    <header v-else class="pos__table-bar">
      <button type="button" class="pos__back" @click="$emit('backToFloor')">
        ← {{ t.t("pos.action.backToFloor") }}
      </button>
      <span class="pos__table-name" data-testid="table-name">{{ tableName }}</span>
      <button type="button" class="pos__back" data-testid="move-table" @click="moving = true">
        {{ t.t("pos.table.move") }}
      </button>
      <!-- The food is on the pass: carried out, it comes off the kitchen's list. -->
      <button
        v-if="activeTable && readyTables?.includes(activeTable)"
        type="button"
        class="pos__back pos__back--attention"
        data-testid="carried-out"
        @click="$emit('handOverTable', activeTable)"
      >
        {{ t.t("pos.table.carried") }}
      </button>
      <span class="pos__who">{{ signedIn?.displayName }}</span>
    </header>

    <OrderView
      v-if="!drawer && !receiptBook && activeTable !== null && activeTable !== undefined"
      :check="check"
      :checks="checks ?? []"
      :menu="menu"
      :t="t"
      :currency="currency"
      :disabled="cannotSell"
      :sold-out="soldOut"
      :guest-url="guestUrl ?? null"
      :courses="courses === true && !(activeTable ?? '').startsWith('pickup:')"
      :course="course"
      @course="course = $event"
      @fire-course="$emit('fireCourse', $event)"
      @ring="ring"
      @customize="choosing = $event"
      @select-check="$emit('selectCheck', $event)"
      @split-evenly="$emit('splitEvenly', $event)"
      @unsplit="$emit('unsplit')"
      @guest-code="showingGuestCode = true"
      @void="voiding = $event"
      @allowance="allowing = $event"
      @move-selection="$emit('moveSelection', $event)"
      @quantity="$emit('quantity', $event)"
      @set-mode="$emit('setMode', $event)"
      @send="$emit('send')"
      @pay="paying = true"
      @buyer="naming = true"
    />

    <!-- Rendered inline rather than in a portal: the till is a single full-screen
         surface and a portal would only add a focus-management problem. -->
    <!--
      THE AGE CHECK, above everything else that can be open.
      It is the only dialog here that exists to produce a legal record rather
      than to collect an input, and a server should not be able to reach past it
      to the check behind.
    -->
    <AgeCheckDialog
      v-if="ageCheck"
      :t="t"
      :items="ageCheck.items"
      :busy="ageBusy === true"
      @confirm="emit('confirmAge')"
      @cancel="emit('cancelAge')"
    />

    <!--
      THE ÚČTENKA, at the moment a server would tear it off the printer.

      Outside the `signedIn` branch deliberately: closing a check is the last
      thing a shift does, and a sign-out that raced the dialog would leave
      somebody holding a receipt the screen had thrown away.
    -->
    <ReceiptDialog
      v-if="receipt"
      :t="t"
      :lines="receipt.lines"
      :refusal-key="receipt.refusalKey"
      :warning-keys="receipt.warningKeys"
      :pickup-code="pickupShown ?? null"
      :printer-name="receiptPrinter ?? null"
      :print-status="receiptPrint ?? null"
      @close="emit('closeReceipt')"
      @print="emit('printReceipt')"
    />

    <GuestCodeDialog
      v-if="showingGuestCode"
      :url="guestUrl ?? null"
      :t="t"
      @close="showingGuestCode = false"
    />

    <AllowanceDialog
      v-if="allowing && allowingLine"
      :line-name="allowingLine.name"
      :gross-minor="allowingLine.grossMinor"
      :t="t"
      :currency="currency"
      :needs-approval="allowanceNeedsApproval"
      :approvers="approvers"
      :comp-needs-approval="compNeedsApproval"
      :comp-approvers="compApprovers"
      @cancel="allowing = null"
      @confirm="confirmAllowance"
    />

    <BuyerDialog
      v-if="naming"
      :t="t"
      :due-minor="check.totals.dueMinor"
      :simplified-limit-minor="simplifiedLimitMinor ?? null"
      :currency="currency"
      :busy="buyerBusy ?? false"
      :notice="buyerNotice ?? null"
      :found="buyerFound ?? null"
      @lookup="$emit('lookupBuyer', $event)"
      @confirm="
        (payload) => {
          $emit('identifyBuyer', payload);
          naming = false;
        }
      "
      @cancel="naming = false"
    />

    <ModifierSheet
      v-if="choosingItem"
      :item="choosingItem"
      :mode="check.consumptionMode"
      :t="t"
      :currency="currency"
      @cancel="choosing = null"
      @confirm="confirmChoices"
    />

    <TransferDialog
      v-if="moving && activeTable"
      :floor="floor ?? []"
      :current="activeTable"
      :t="t"
      @cancel="moving = false"
      @move="
        (to) => {
          moving = false;
          $emit('transfer', to);
        }
      "
    />

    <VoidDialog
      v-if="voiding"
      :line-name="voidingName"
      :t="t"
      :needs-approval="voidNeedsApproval"
      :approvers="approvers"
      @cancel="voiding = null"
      @confirm="confirmVoid"
    />

    <TenderDialog
      v-if="paying"
      :check="check"
      :t="t"
      :currency="currency"
      :cash-increment-minor="cashIncrementMinor"
      @tender="
        (payload) => {
          $emit('tender', payload);
          // Taken and closing: the receipt comes next, not this dialog again.
          if (payload.closeAfter) paying = false;
        }
      "
      @reverse="$emit('reverseTender', $event)"
      @close="closeAndDismiss"
      @cancel="paying = false"
    />
    </template>
  </div>
</template>

<style scoped>
.pos {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  font-size: var(--gf-text-base);
  color: var(--gf-color-text);
  background: var(--gf-color-canvas);
}

.pos__floor {
  flex: 1;
  min-height: 0;
}

/*
   THE TOP STRIP: the status line, and on the screens that have it the
   language. One hairline under both, so they read as one row.
*/
.pos__top {
  display: flex;
  align-items: stretch;
  flex: none;
  background: var(--gf-color-canvas);
  border-bottom: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.pos__banner {
  flex: 1;
  min-width: 0;
}

/*
   Used twice in a venue's life, so it sits at the end of the strip, away from
   the check panel and the product grid where every other tap lands — but at a
   real touch size now (48 px, the space token nearest the 44 px floor), not
   the 44 × 28 it used to float at. MyMonday's segmented control: a sunken
   track, the chosen segment raised out of it.
*/
.pos__lang {
  display: flex;
  align-self: center;
  flex: none;
  gap: var(--gf-space-0);
  margin: var(--gf-space-1) var(--gf-space-2) var(--gf-space-1) auto;
  padding: var(--gf-space-0);
  background: var(--gf-color-surface-sunken);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
}

.pos__lang-btn {
  min-width: var(--gf-space-7);
  min-height: var(--gf-space-7);
  border: 0;
  border-radius: var(--gf-radius-sm);
  background: transparent;
  color: var(--gf-color-text-muted);
  font: inherit;
  font-size: var(--gf-text-xs);
  font-weight: var(--gf-weight-semibold);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .pos__lang-btn:hover {
    color: var(--gf-color-text);
  }
}

/* `aria-pressed` carries the state to a screen reader; the raised segment
   carries it to everyone else. Neutral: a chosen language is not an action. */
.pos__lang-btn[aria-pressed="true"] {
  background: var(--gf-color-surface-raised);
  color: var(--gf-color-text);
  box-shadow: var(--gf-shadow-sm), inset 0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle);
}

.pos__lang-btn:focus-visible,
.pos__back:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.pos__table-bar {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  padding: var(--gf-space-2) var(--gf-space-4);
  background: var(--gf-color-surface);
  border-bottom: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

/*
  ONE LINE, whatever the width. On a phone the bar wrapped every item onto two
  lines — "←" over "Stoly", "Účet" over "4" — so the buttons stay whole, the
  table's name gives way with an ellipsis, and who is signed in goes last.
*/
.pos__table-name {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: var(--gf-text-md);
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pos__who {
  flex: 0 1 auto;
  min-width: 0;
  margin-left: auto;
  overflow: hidden;
  color: var(--gf-color-text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pos__table-bar .pos__back {
  flex: none;
  white-space: nowrap;
}

@media (max-width: 30rem) {
  .pos__table-bar {
    gap: var(--gf-space-2);
    padding-inline: var(--gf-space-3);
  }

  .pos__table-bar .pos__back {
    padding-inline: var(--gf-space-3);
  }
}

.pos__back {
  display: inline-flex;
  align-items: center;
  gap: var(--gf-space-2);
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface);
  box-shadow: var(--gf-shadow-sm);
  color: var(--gf-color-text);
  font: inherit;
  font-weight: var(--gf-weight-medium);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

@media (hover: hover) {
  .pos__back:hover {
    background: var(--gf-color-surface-hover);
  }
}

.pos__back:active {
  background: var(--gf-color-surface-active);
  box-shadow: none;
}

/* "Count in first": a small accent pip rather than a red ring — the accent
   is the pay button's colour now, and a whole button tinted red on the floor
   would read as the thing to press. The words carry the state. */
.pos__back--attention {
  font-weight: var(--gf-weight-semibold);
}

.pos__back--attention::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 3px;
  background: var(--gf-color-accent);
}

/* Error styling comes from the design tokens, whose contrast pairs are
   verified by test in all three themes — including high-contrast, where a
   red-on-dark banner is otherwise the first thing to become unreadable. A
   solid bar, because it is a blocking state rather than a status. */
.pos__error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-2);
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  background: var(--gf-color-danger);
  color: var(--gf-color-on-danger);
  font-weight: var(--gf-weight-medium);
}

.pos__error-dismiss {
  min-height: var(--gf-target-min);
  min-width: var(--gf-target-min);
  padding: 0 var(--gf-space-3);
  border: var(--gf-border-thin) solid currentColor;
  border-radius: var(--gf-radius-md);
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
</style>
